import * as React from 'react';
import { Dispatch } from 'react';
import { Section } from './section';
import { LiteralSelectField } from './literal-select-field';
import { BooleanSelectField } from './boolean-select-field';
import { EmptyPacingImage } from '@/features/line-items/line-item-form/ui/empty-pacing-image';
import {
  PacingBlockRow,
  PriceAllocationSummary
} from '@/features/line-items/line-item-form/common/selectors';
import { ISODateRange } from 'shared/src/date-string-utils/types';
import { CommonAction } from '@/features/line-items/line-item-form/common/form-reducer';
import { CustomPacingField } from '@/features/line-items/line-item-form/ui/custom-pacing-field';
import { LineItemType, PacingType } from 'shared/src/domain/line-item/line-item-types';
import { PacingValidatorSpend } from 'shared/src/domain/line-item/pacing-validator';

const PACING_TYPE_OPTIONS: PacingType[] = ['lifetime', 'custom'] as const;

const MONTHLY_ROLLOVER_OPTIONS = [
  { value: true, label: 'Rollover', description: <RolloverDesc /> },
  { value: false, label: 'No Rollover', description: <NoRolloverDesc /> }
] as const;

export type PacingConfig = {
  lineItemType: LineItemType;
  rows: PacingBlockRow[];
  spend: PacingValidatorSpend;
  allocation: PriceAllocationSummary;
  monthly_rollover: boolean;
  pacing_amount: number | undefined;
  unit_cost: number | undefined;
  dates: ISODateRange;
  pacing_type: PacingType;
  label: string;
  unit_label: string;
  is_published: boolean;
};

type Props = {
  config: PacingConfig;
  dispatch: Dispatch<CommonAction>;
  pacingDescription: React.ReactNode;
  errorFor?: (field: string) => string | undefined;
  warningFor?: (field: string) => string | undefined;
};

export function Pacing({ config, dispatch, pacingDescription, errorFor, warningFor }: Props) {
  const hasSpend = config.spend.some(r => (r.amount ?? 0) > 0);
  const pacingTypeDisabled = config.is_published && hasSpend && config.pacing_type !== 'lifetime';
  return (
    <Section heading="Pacing" description={pacingDescription}>
      {config.pacing_amount === undefined ? (
        <EmptyPacing lineItemType={config.lineItemType} />
      ) : (
        <>
          <div className="flex items-start gap-8">
            <LiteralSelectField
              label="Pacing Type"
              value={config.pacing_type}
              options={PACING_TYPE_OPTIONS}
              onChange={value => dispatch({ type: 'PACING_SET_TYPE', value })}
              disabled={pacingTypeDisabled}
            />
            {config.pacing_type === 'custom' ? (
              <BooleanSelectField
                label="Monthly Rollover"
                value={config.monthly_rollover}
                options={MONTHLY_ROLLOVER_OPTIONS}
                onChange={() => dispatch({ type: 'PACING_TOGGLE_MONTHLY_ROLLOVER' })}
              />
            ) : null}
          </div>
          <CustomPacingField
            config={config}
            dispatch={dispatch}
            errorFor={errorFor}
            warningFor={warningFor}
          />
        </>
      )}
    </Section>
  );
}

function EmptyPacing({ lineItemType }: { lineItemType: LineItemType }) {
  return (
    <div className="flex w-full flex-col items-center justify-center p-2">
      <EmptyPacingImage />
      <div className="mt-4 text-lg font-extralight text-gray-600">
        <EmptyPacingPrompt lineItemType={lineItemType} />
      </div>
    </div>
  );
}

function EmptyPacingPrompt({ lineItemType }: { lineItemType: LineItemType }) {
  if (lineItemType === 'zero_dollar') {
    return (
      <>
        To define a pacing strategy, select a Channel, Tactic, and Unit Price Type, and enter{' '}
        <span className="font-normal">Estimated Units.</span>
      </>
    );
  }
  if (lineItemType === 'management_fee') {
    return (
      <>
        To define a pacing strategy, set the line item's{' '}
        <span className="font-normal">Media Budget.</span>
      </>
    );
  }
  return (
    <>
      To define a pacing strategy, set the line item's <span className="font-normal">Price.</span>
    </>
  );
}

function NoRolloverDesc() {
  return (
    <span className="text-sm">
      Under-delivery <span className="font-bold">cannot</span> be made up
    </span>
  );
}

function RolloverDesc() {
  return (
    <span className="text-sm">
      Under-delivery <span className="font-bold">can</span> be made up in the following months
    </span>
  );
}
